import styled from 'styled-components';
import {
  Button,
  Heading,
  Toast,
  ToastType,
  SearchInput,
  IconButton,
  Select,
  Option,
  Tag,
  TagText,
} from '@alefeducation/ui-elements-library-core';
import Download from '@alefeducation/ui-elements-library-icons/lib/components/Icons/Download';
import Upload from '@alefeducation/ui-elements-library-icons/lib/components/Icons/Upload';
import { Filter } from '@alefeducation/ui-elements-library-icons';
import { useHistory } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import {
  useProductCourseMapping,
  useProductCourseMappingTotalCount,
  useProductMappingsFilters,
} from './useProductCourseMapping';
import { ProductCourseMappingSchema } from './types';
import { PRODUCT_COURSE_MAP } from '../../utils/constants';
import { PRODUCT_COURSE_MAPPING } from '../../components/Routing/routeHelpers';
import appLocalStorage from '../../utils/AppLocalStorage';
import ExportCsvModal from '../../components/ExportCsvModal/ExportCsvModal';
import { ToastContentType } from '../BulkUpload/BulkUpload';
import { fetchApiPost } from '../../utils/fetchUtils';
import DataGrid from '../../components/DataGrid/DataGrid';

export const PRODUCT_COURSE_MAPPING_EXPORT =
  '/organization-service/api/jobs/export/product-mapping/csv';

const getDefaultDateFormat = (date: number) =>
  moment(date).format('Do MMM, YYYY h:mm a');

const capitalizeFirstLetter = (text: string) => {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const columns = [
  {
    header: 'Product Name',
    accessorKey: 'productName',
    size: 200,
  },
  {
    header: 'Product Code',
    size: 180,
    accessorKey: 'productCode',
  },
  {
    header: 'Course Name',
    accessorKey: 'courseName',
  },
  {
    header: 'Course Code',
    accessorKey: 'courseCode',
  },
  {
    header: 'Added by',
    size: 200,
    accessorKey: 'createdBy',
  },
  {
    header: 'Added On',
    size: 132,
    accessorKey: 'createdAt',
    cell: (info: any) => {
      return getDefaultDateFormat(Number(info.getValue()));
    },
  },
  {
    header: 'Last Modified On',
    size: 132,
    accessorKey: 'lastModifiedDate',
    cell: (info: any) => {
      return getDefaultDateFormat(Number(info.getValue()));
    },
  },
];

const ProductCourseMapping = () => {
  const history = useHistory();
  const [toastData, setToastData] = useState<ToastContentType>({
    type: ToastType.SUCCESS,
    title: '',
    description: '',
  });
  const [showToast, setShowToast] = useState<boolean>(false);
  const [openExportCsvPopup, setOpenExportCsvPopup] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [productType, setProductType] = useState<string>('');
  const [addedBy, setAddedBy] = useState<string>('');
  const [filters, setFilters] = useState({
    page: 1,
    size: 20,
    search: '',
    productType: '',
    addedBy: '',
  });

  const {
    productCourseMappingsData,
    fetchAllProductCourseMappings,
    isFetchLoading,
  } = useProductCourseMapping();
  const { productMappingsFilters } = useProductMappingsFilters();
  const totalCount = useProductCourseMappingTotalCount();
  const onToastHide = (): void => {
    setShowToast(false);
    setToastData({
      type: ToastType.SUCCESS,
      title: '',
      description: '',
    });
  };

  const onClickExport = async (email: string) => {
    const showError = () => {
      setShowToast(true);
      setToastData({
        type: ToastType.ERROR,
        title: 'Export Failed',
        description: '',
      });
    };
    const res = await fetchApiPost(PRODUCT_COURSE_MAPPING_EXPORT, {
      email,
    });
    try {
      const data = await res.json();
      if (res.ok) {
        setShowToast(true);
        setToastData({
          type: ToastType.SUCCESS,
          title: 'Export started. Email will be sent shortly.',
          description: '',
        });
      } else {
        showError();
      }
      return { success: res.ok, message: data.message };
    } catch {
      showError();
    }
  };

  const exportCsv = (email: string) => {
    onClickExport(email);
    setOpenExportCsvPopup(false);
  };

  const fetchData = useCallback(
    async (
      page: number,
      size: number,
      search: string,
      productType: string,
      addedBy: string,
    ) => {
      fetchAllProductCourseMappings({
        variables: {
          pageNumber: page - 1,
          pageSize: size,
          search,
          productType,
          addedBy,
        },
      });
    },
    [fetchAllProductCourseMappings],
  );

  useEffect(() => {
    fetchData(
      filters.page,
      filters.size,
      filters.search,
      filters.productType === 'All' ? '' : filters.productType,
      filters.addedBy === 'All' ? '' : filters.addedBy,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.page,
    filters.size,
    filters.search,
    filters.productType,
    filters.addedBy,
  ]);

  const onPageChange = (page: number) => setFilters({ ...filters, page });
  const onPageSizeChange = (size: number) =>
    setFilters({ ...filters, page: 1, size });

  const onSearchChange = (value: string) => {
    setSearch(value);
    setFilters({
      ...filters,
      search: value,
      page: 1,
    });
  };

  const onProductTypeChange = (_id: string, name: string) => {
    setProductType(name);
    setFilters({
      ...filters,
      productType: name,
      page: 1,
    });
  };

  const onAddedByChange = (_id: string, name: string) => {
    setAddedBy(name);
    setFilters({
      ...filters,
      addedBy: name,
      page: 1,
    });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
    if (showFilters) {
      setProductType('');
      setAddedBy('');
      setFilters({
        ...filters,
        productType: '',
        addedBy: '',
        page: 1,
      });
    }
  };

  return (
    <MappingContainer>
      <StyledContainer
        data-testid="mapping-container-header"
        id="mapping-container-header"
      >
        <Heading variant="heading-small" color="primary.100">
          {PRODUCT_COURSE_MAP}
        </Heading>
        {!appLocalStorage.isViewOnlyRole() && (
          <HeaderActions>
            <Button
              variant="ghost"
              size="lg"
              data-testid="export-btn"
              onClick={() => setOpenExportCsvPopup(!openExportCsvPopup)}
              icon={<Download />}
            >
              Export
            </Button>
            <Button
              variant="secondary"
              size="lg"
              data-testid="bulk-upload-btn"
              onClick={() => history.push(PRODUCT_COURSE_MAPPING.bulkUpload)}
              icon={<Upload />}
            >
              Bulk Upload
            </Button>
          </HeaderActions>
        )}
      </StyledContainer>
      <PageContainer>
        <TopHeader>
          <TitleContainer>
            <h1>Product Course Mapping</h1>
            <Tag>
              <TagText>{totalCount.toString()}</TagText>
            </Tag>
          </TitleContainer>
          <SearchContainer>
            <Search>
              <SearchInput
                width="400px"
                value={search}
                placeholder="Search..."
                onChange={(e) => {
                  onSearchChange(e.target.value);
                }}
                onClear={() => {
                  setSearch('');
                  onSearchChange('');
                }}
                onSearch={() => {}}
              />
            </Search>
            <FilterButtonContainer>
              <IconButton
                className={showFilters ? 'active' : ''}
                data-testid="filter-button"
                onClick={toggleFilters}
                size="large"
                tooltipPosition="bottom"
                tooltipContent="Filter"
              >
                <Filter color="#485465" />
              </IconButton>
            </FilterButtonContainer>
          </SearchContainer>
        </TopHeader>
        <Filters>
          {showFilters && (
            <>
              <FilterDropdown>
                <FilterLabel>Product Type</FilterLabel>
                <Select
                  data-testid="select-product-type"
                  selectedOption={productType}
                  onSelectChange={onProductTypeChange}
                  placeholder="Select"
                >
                  {['All', ...productMappingsFilters.productTypes].map(
                    (type) => (
                      <Option key={type}>{capitalizeFirstLetter(type)}</Option>
                    ),
                  )}
                </Select>
              </FilterDropdown>
              <FilterDropdown>
                <FilterLabel>Added By</FilterLabel>
                <Select
                  data-testid="select-added-by"
                  selectedOption={addedBy}
                  onSelectChange={onAddedByChange}
                  placeholder="Select"
                >
                  {['All', ...productMappingsFilters.createdBy].map((user) => (
                    <Option key={user}>{capitalizeFirstLetter(user)}</Option>
                  ))}
                </Select>
              </FilterDropdown>
            </>
          )}
        </Filters>
        <StyledTable>
          <TableContainer>
            <DataGrid<ProductCourseMappingSchema>
              isDataLoading={isFetchLoading}
              allowPagination
              data={productCourseMappingsData?.mappings}
              columns={columns}
              page={filters?.page || 1}
              size={filters?.size || 20}
              pageCount={productCourseMappingsData?.totalCount || 0}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              enableColumnTruncation
            />
          </TableContainer>
        </StyledTable>
      </PageContainer>
      {openExportCsvPopup && (
        <ExportCsvModal
          header="Export Product Course Mapping"
          onAction={exportCsv}
          onClose={() => setOpenExportCsvPopup(false)}
        />
      )}
      {showToast && (
        <Toast
          key={`toast-${toastData.type}-${toastData.title}`}
          autoHide
          content={{
            title: toastData.title,
            description: toastData.description,
          }}
          onHide={onToastHide}
          data-testid={`toast-${toastData.type}`}
          type={toastData.type}
        />
      )}
    </MappingContainer>
  );
};

export default ProductCourseMapping;

const PageContainer = styled.div`
  background-color: #fff;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 10px;
`;

const MappingContainer = styled.div`
  height: 100%;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  h1 {
    font-size: 18px;
    font-weight: 700;
    color: #485465;
  }
`;

const TopHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  gap: 16px;
`;

const Filters = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
  padding: 0px 24px;
`;

const FilterButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

const FilterDropdown = styled.div`
  width: 287px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 16px;
  font-weight: 300;
  color: ${({ theme }: any) => theme.colors?.text?.primary || '#485465'};
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Search = styled.div`
  padding-top: 0px;
`;

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  height: 40px;
`;

export const StyledTable = styled.div`
  background: ${({ theme }: any) => theme.colors.primary[0]};

  .loading,
  .empty-message-view {
    height: 80%;
  }
`;

const TableContainer = styled.div`
  .alef-data-grid__pagination {
    position: sticky;
    bottom: 0;
    background-color: #fff;
  }

  .alef-data-grid table thead {
    background-color: #fff;
    position: sticky;
    top: 0;
    z-index: 5;
  }
`;
